/**
 * remarkCallouts — GFM alert syntax without a new dependency (#930).
 *
 * Detects the GitHub alert form inside blockquotes:
 *
 *   > [!NOTE]
 *   > Body text…
 *
 * and tags the blockquote with `data-callout="<kind>"` (via hProperties) so
 * ChatMarkdown's `blockquote` component can render a `Callout` instead of a
 * plain quote. The marker line itself is stripped from the tree, matching
 * GitHub's behavior of replacing it with the alert title.
 *
 * Per the GFM spec the marker must be UPPERCASE and alone on the first line of
 * the blockquote — `> [!NOTE] inline text` is NOT an alert and renders as an
 * ordinary quote, which is also what keeps false positives out of quoted
 * message content.
 *
 * This is a hand-rolled mdast walk on purpose: `unist-util-visit` is only a
 * transitive dependency here, and the shape we need (blockquote → first
 * paragraph → first text node) is trivial to address directly.
 */

/** Minimal structural mdast shape — enough for the walk, no @types/mdast import. */
interface MdNode {
  type: string;
  value?: string;
  children?: MdNode[];
  data?: { hProperties?: Record<string, unknown> } & Record<string, unknown>;
}

export const CALLOUT_KINDS = ['note', 'tip', 'warning', 'caution'] as const;
export type CalloutKind = (typeof CALLOUT_KINDS)[number];

const MARKER = /^\[!(NOTE|TIP|WARNING|CAUTION)\][ \t]*(?:\r?\n|$)/;

function tagBlockquote(bq: MdNode): void {
  const para = bq.children?.[0];
  if (!para || para.type !== 'paragraph' || !para.children?.length) return;
  const text = para.children[0];
  if (text.type !== 'text' || typeof text.value !== 'string') return;

  const m = MARKER.exec(text.value);
  if (!m) return;

  const kind = m[1].toLowerCase() as CalloutKind;
  text.value = text.value.slice(m[0].length);
  if (text.value === '') {
    para.children.shift();
    // A hard break right after the marker would otherwise render a leading <br>.
    while (para.children[0]?.type === 'break') para.children.shift();
    if (para.children.length === 0) bq.children!.shift();
  }

  bq.data = {
    ...bq.data,
    hProperties: { ...bq.data?.hProperties, dataCallout: kind },
  };
}

function walk(node: MdNode): void {
  if (node.type === 'blockquote') tagBlockquote(node);
  if (node.children) for (const child of node.children) walk(child);
}

/** Remark plugin: () => (tree) => void. */
export function remarkCallouts() {
  return (tree: unknown) => {
    walk(tree as MdNode);
  };
}
